import Alert from "@cloudscape-design/components/alert"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Cards from "@cloudscape-design/components/cards"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import TextContent from "@cloudscape-design/components/text-content"
import Textarea from "@cloudscape-design/components/textarea"
import React, { useState } from "react"
import { useComments, useUpdateComment } from "@/hooks/queries/useCollaboration"
import { isForbidden } from "@/utils/apiErrors"
import { useDtaComments } from "../DTAEditor/comments/DtaCommentsContext"
import { formatDateTime } from "./helpers"

interface CommentsSectionProps {
  dtaId: string
  isDAE?: boolean
  onCommentUpdated?: () => void
  version: string
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  dtaId,
  isDAE: _isDAE = false,
  onCommentUpdated,
  version,
}) => {
  const { data, isPending, error: fetchError } = useComments(dtaId, version)
  const updateComment = useUpdateComment(dtaId)
  const comments_ctx = useDtaComments()
  const [resolutionText, setResolutionText] = useState("")
  const [showResolutionForm, setShowResolutionForm] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  /*
   * Treat a 403 on the read as "nothing to show": render the normal empty state rather than the raw
   * error payload, so the user never sees `Forbidden - {"error":"Forbidden"}`.
   */
  const error = localError || (fetchError && !isForbidden(fetchError) ? fetchError.message : null)
  const comments = data?.comments ?? []

  const handleResolve = async (commentId: string, status: "RESOLVED" | "REJECTED") => {
    if (!resolutionText.trim() && status === "REJECTED") {
      setLocalError("Please provide a reason for rejection")
      return
    }

    setLocalError(null)
    try {
      await updateComment.mutateAsync({
        commentId,
        status,
        resolutionComment: resolutionText.trim() || undefined,
      })
      setShowResolutionForm(null)
      setResolutionText("")
      onCommentUpdated?.()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to update comment")
    }
  }

  const handleReply = async (commentId: string) => {
    if (!resolutionText.trim()) return

    setLocalError(null)
    try {
      await updateComment.mutateAsync({
        commentId,
        status: "PENDING",
        resolutionComment: resolutionText.trim(),
      })
      setShowResolutionForm(null)
      setResolutionText("")
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Failed to add reply")
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Badge color="blue">Pending</Badge>
      case "RESOLVED":
        return <Badge color="green">Resolved</Badge>
      case "REJECTED":
        return <Badge color="red">Rejected</Badge>
      default:
        return null
    }
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )

  if (isPending) {
    return (
      <Container variant="stacked">
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner />
            <Box color="text-body-secondary">Loading discussions...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    )
  }

  return (
    <Container
      variant="stacked"
      header={<Header counter={`(${comments.length})`}>Discussions</Header>}
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}

        <Cards
          items={sortedComments}
          trackBy="comment_id"
          entireCardClickable={false}
          cardsPerRow={[{ cards: 1 }]}
          empty={
            <Box textAlign="center" padding={{ vertical: "xl" }} color="text-body-secondary">
              <TextContent>
                <p>No discussions yet.</p>
              </TextContent>
            </Box>
          }
          cardDefinition={{
            header: (comment) => (
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Box variant="strong">{comment.user_id}</Box>
                {comment.section_reference && comment.section_reference !== "General" && (
                  <Box color="text-body-secondary">
                    {"·"} {comment.section_reference}
                  </Box>
                )}
                {getStatusBadge(comment.status)}
                <Box color="text-body-secondary" fontSize="body-s">
                  {"·"} {formatDateTime(comment.created_at)}
                </Box>
              </SpaceBetween>
            ),
            sections: [
              {
                id: "body",
                content: (comment) => (
                  <SpaceBetween size="s">
                    <TextContent>
                      <p style={{ whiteSpace: "pre-wrap" }}>{comment.comment_text}</p>
                    </TextContent>

                    {/* View: navigate to the anchored section/field AND isolate this thread in the rail. */}
                    {comments_ctx && (
                      <Button
                        variant="inline-link"
                        iconName="external"
                        onClick={() => {
                          if (
                            comment.section_reference &&
                            comment.section_reference !== "General"
                          ) {
                            comments_ctx.navigateToAnchor(comment.section_reference)
                          }
                          comments_ctx.openRailForComment(comment.comment_id)
                        }}
                      >
                        View
                      </Button>
                    )}

                    {comment.replies &&
                      comment.replies.length > 0 && (
                        // Each reply is a distinct indented, tinted box so replies read as separate
                        // nested responses under the parent comment (not one merged block of text).
                        <div style={{ marginLeft: "16px" }}>
                          <SpaceBetween size="xs">
                            {comment.replies.map((reply, idx) => (
                              <div
                                key={idx}
                                style={{
                                  background: "var(--color-background-status-info, #f0f8ff)",
                                  border: "1px solid var(--color-border-divider-default)",
                                  borderRadius: "8px",
                                  padding: "8px 12px",
                                }}
                              >
                                <SpaceBetween size="xxs">
                                  <Box color="text-body-secondary" fontSize="body-s">
                                    <Box variant="strong" display="inline" fontSize="body-s">
                                      {reply.author}
                                    </Box>{" "}
                                    {"·"} {formatDateTime(reply.timestamp)}
                                  </Box>
                                  <TextContent>
                                    <p style={{ whiteSpace: "pre-wrap" }}>{reply.text}</p>
                                  </TextContent>
                                </SpaceBetween>
                              </div>
                            ))}
                          </SpaceBetween>
                        </div>
                      )}

                    {(!comment.replies || comment.replies.length === 0) && comment.resolved_at && (
                      <SpaceBetween size="xxs">
                        <Box color="text-body-secondary" fontSize="body-s">
                          {comment.status === "PENDING" ? "Reply" : comment.status} by{" "}
                          {comment.resolved_by} {"·"} {formatDateTime(comment.resolved_at)}
                        </Box>
                        {comment.resolution_comment && (
                          <TextContent>
                            <p>
                              <em>{comment.resolution_comment}</em>
                            </p>
                          </TextContent>
                        )}
                      </SpaceBetween>
                    )}

                    {comment.status === "PENDING" &&
                      (showResolutionForm === comment.comment_id ? (
                        <SpaceBetween size="s">
                          <Textarea
                            value={resolutionText}
                            onChange={({ detail }) => setResolutionText(detail.value)}
                            placeholder="Your response..."
                            rows={2}
                          />
                          <SpaceBetween size="xs" direction="horizontal">
                            <Button
                              variant="primary"
                              onClick={() => void handleReply(comment.comment_id)}
                              disabled={updateComment.isPending || !resolutionText.trim()}
                            >
                              Comment
                            </Button>
                            <Button
                              onClick={() => void handleResolve(comment.comment_id, "RESOLVED")}
                              disabled={updateComment.isPending}
                            >
                              Resolve
                            </Button>
                            <Button
                              variant="link"
                              onClick={() => {
                                setShowResolutionForm(null)
                                setResolutionText("")
                              }}
                            >
                              Cancel
                            </Button>
                          </SpaceBetween>
                        </SpaceBetween>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => setShowResolutionForm(comment.comment_id)}
                        >
                          Comment
                        </Button>
                      ))}
                  </SpaceBetween>
                ),
              },
            ],
          }}
        />
      </SpaceBetween>
    </Container>
  )
}

export default CommentsSection
